import { ListResponse } from "../infrastructure/useFetch";
import { useCallback, useState } from "react";
import { useLoading } from "./use-loading";

type Params<T, P extends { offset?: number; limit?: number }> = {
  showInPage: number;
  fetchCallback: (params: P, url?: string) => Promise<ListResponse<T>>;
  hookAfterFetch?: (params: P | undefined, response: ListResponse<T>) => ListResponse<T> | any;
  isInitLoadingState?: boolean;
};

export type ApiExecutorContentInfo = {
  isInitialLoading: boolean;
  isPageLoading: boolean;
  isMoreLoading: boolean;
  allContentCounts: number;
  contentsCountWithOffset: number;
  currentPage: number;
  canLoadMore: boolean;
};

export type ApiExecutorData<
  T,
  P extends { offset?: number; limit?: number },
> = {
  contents: T[];
  info: ApiExecutorContentInfo;
  fetch: (params: P) => Promise<void>;
  fetchMore: () => Promise<void>;
  fetchPage: (page: number, params?: P) => Promise<void>;
  filters: P | null;
};

export function useApiExecutor<
  T,
  P extends { offset?: number; limit?: number },
>({ showInPage, fetchCallback, hookAfterFetch, isInitLoadingState = true }: Params<T, P>): ApiExecutorData<T, P> {
  const [contents, setContents] = useState<T[]>([]);
  const [lastResponse, setLastResponse] = useState<ListResponse<T> | null>(
    null
  );
  const [lastLoadedPage, setLastLoadedPage] = useState<number>(1);
  const [lastUsedParams, setLastUsedParams] = useState<P | null>(null);
  const { loadings, startLoading, stopLoading } = useLoading({
    fetch: isInitLoadingState,
    fetchPage: false,
    fetchMore: false
  });

  const fetch = useCallback(async (params: P) => {
    try {
      startLoading("fetch");
      let response = await fetchCallback({
        ...params,
        limit: showInPage,
        offset: 0
      });

      if (hookAfterFetch) {
        response = hookAfterFetch(params, response);
      }

      setLastResponse(response);
      setContents(Array.isArray(response) ? response : response.results);
      if (params) {
        setLastUsedParams(params);
      }
    } catch (e) {
    } finally {
      stopLoading("fetch");
    }
  }, [fetchCallback]);

  const fetchPage = useCallback(
    async (page: number, params?: P) => {
      try {
        startLoading("fetchPage");
        const offset = showInPage * (page - 1);
        let response = await fetchCallback({
          ...(params ?? (lastUsedParams as P) ?? {}),
          limit: showInPage,
          offset,

          rows_to_show: showInPage,
          page_number: page
        });
        setLastResponse(response);
        setLastLoadedPage(page);

        if (hookAfterFetch) {
          response = hookAfterFetch(params, response);
        }

        setContents(response.results);
      } catch (e) {
      } finally {
        stopLoading("fetchPage");
      }
    },
    [lastUsedParams, fetchCallback]
  );

  const fetchMore = useCallback(async () => {
    try {
      startLoading("fetchMore");
      const response = await fetchCallback(
        lastUsedParams as P,
        lastResponse?.next ?? undefined
      );
      setLastResponse(response);
      setContents((prev) => [...prev, ...response.results]);
    } catch (e) {
    } finally {
      stopLoading("fetchMore");
    }
  }, [lastUsedParams, lastResponse, fetchCallback]);

  return {
    contents,
    fetch,
    fetchPage,
    fetchMore,
    filters: lastUsedParams,
    info: {
      isInitialLoading: loadings.fetch,
      isPageLoading: loadings.fetchPage,
      isMoreLoading: loadings.fetchMore,
      allContentCounts: lastResponse?.count ?? 0,
      canLoadMore: lastResponse?.next != null,
      contentsCountWithOffset:
        lastLoadedPage != null
          ? showInPage * lastLoadedPage - (showInPage - contents.length)
          : 0,
      currentPage: lastLoadedPage
    }
  };
}
